import React, { useEffect, useMemo, useState } from 'react';
import {
  getCollaborators,
  getWorkspaceMembers,
  addCollaborator,
  removeCollaborator,
  transferOwner,
  Collaborator,
  ProjectCollaborators,
  WorkspaceMember,
} from '../lib/api';
import { I } from './Shared';

/**
 * Manage who a project is shared with inside the workspace. Owners can add /
 * remove collaborators (editor or viewer), and transfer ownership. Non-owners
 * see a read-only view of who has access.
 */
export function ShareModal({
  projectId,
  appName,
  canManage,
  onClose,
  onChanged,
}: {
  projectId: string;
  appName: string;
  /** Only the owner (or an admin) may mutate the collaborator set. */
  canManage: boolean;
  onClose: () => void;
  /** Called after any change so the parent can refresh its list. */
  onChanged?: () => void;
}) {
  const [data, setData] = useState<ProjectCollaborators | null>(null);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [pickUser, setPickUser] = useState('');
  const [pickRole, setPickRole] = useState<'editor' | 'viewer'>('editor');

  async function reload() {
    const [c, m] = await Promise.all([getCollaborators(projectId), getWorkspaceMembers()]);
    setData(c);
    setMembers(m);
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [c, m] = await Promise.all([getCollaborators(projectId), getWorkspaceMembers()]);
        if (!cancelled) {
          setData(c);
          setMembers(m);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'failed to load sharing');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Members who can still be added: not the owner, not already a collaborator.
  const addable = useMemo(() => {
    if (!data) return [];
    const taken = new Set<string>([
      ...(data.owner ? [data.owner.userId] : []),
      ...data.collaborators.map((c) => c.userId),
    ]);
    return members.filter((m) => !taken.has(m.id));
  }, [data, members]);

  async function doAdd() {
    if (!pickUser) return;
    setBusy(true);
    setError(null);
    try {
      const collaborators = await addCollaborator(projectId, pickUser, pickRole);
      setData((d) => (d ? { ...d, collaborators } : d));
      setPickUser('');
      onChanged?.();
    } catch (e: any) {
      setError(e?.message ?? 'failed to add');
    } finally {
      setBusy(false);
    }
  }

  async function doRemove(userId: string) {
    setBusy(true);
    setError(null);
    try {
      const collaborators = await removeCollaborator(projectId, userId);
      setData((d) => (d ? { ...d, collaborators } : d));
      onChanged?.();
    } catch (e: any) {
      setError(e?.message ?? 'failed to remove');
    } finally {
      setBusy(false);
    }
  }

  async function doChangeRole(userId: string, role: 'editor' | 'viewer') {
    setBusy(true);
    setError(null);
    try {
      const collaborators = await addCollaborator(projectId, userId, role); // upsert
      setData((d) => (d ? { ...d, collaborators } : d));
      onChanged?.();
    } catch (e: any) {
      setError(e?.message ?? 'failed to update role');
    } finally {
      setBusy(false);
    }
  }

  async function doTransfer(userId: string, label: string) {
    if (!window.confirm(`Transfer ownership of "${appName}" to ${label}? You will become an editor.`)) return;
    setBusy(true);
    setError(null);
    try {
      await transferOwner(projectId, userId);
      await reload();
      onChanged?.();
    } catch (e: any) {
      setError(e?.message ?? 'failed to transfer');
    } finally {
      setBusy(false);
    }
  }

  const label = (m: { name: string | null; email: string | null }) =>
    m.name?.trim() || m.email || 'Unknown';

  return (
    <div className="hm-backdrop" onClick={onClose}>
      <div
        className="af card"
        style={{
          width: 560,
          maxHeight: '86%',
          padding: 0,
          background: 'var(--surface)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px var(--line)',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="loz">SHARE</span>
          <span className="t-h2" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{appName}</span>
          <span style={{ flex: 1 }} />
          <button className="btn btn--ghost btn--sm btn--icon" onClick={onClose}>{I.x}</button>
        </div>

        {error && <div style={{ padding: '10px 20px', color: 'var(--err)', fontSize: 13 }}>{error}</div>}
        {!data && !error && <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-muted)' }}>Loading…</div>}

        {data && (
          <div className="scroll" style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '16px 20px 20px' }}>
            {/* Owner */}
            <div className="t-micro" style={{ marginBottom: 8 }}>OWNER</div>
            <div className="card" style={{ padding: '10px 14px', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontWeight: 600 }}>{data.owner ? label(data.owner) : '—'}</span>
              {data.owner?.email && <span className="t-small muted">{data.owner.email}</span>}
              <span style={{ flex: 1 }} />
              <span className="chip" style={{ fontSize: 11 }}>Owner</span>
            </div>

            {/* Collaborators */}
            <div className="t-micro" style={{ marginBottom: 8 }}>
              COLLABORATORS {data.collaborators.length > 0 && `· ${data.collaborators.length}`}
            </div>
            {data.collaborators.length === 0 ? (
              <div className="card" style={{ padding: 16, textAlign: 'center', color: 'var(--ink-muted)', marginBottom: 18 }}>
                Not shared with anyone yet.
              </div>
            ) : (
              <div className="card" style={{ overflow: 'hidden', marginBottom: 18 }}>
                {data.collaborators.map((c: Collaborator, i) => (
                  <div
                    key={c.userId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 14px',
                      borderBottom: i === data.collaborators.length - 1 ? 'none' : '1px solid var(--line)',
                    }}
                  >
                    <span style={{ fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label(c)}</span>
                    {c.email && <span className="t-small muted" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</span>}
                    <span style={{ flex: 1 }} />
                    {canManage ? (
                      <>
                        <select
                          className="input"
                          value={c.role}
                          disabled={busy}
                          onChange={(e) => void doChangeRole(c.userId, e.target.value as 'editor' | 'viewer')}
                          style={{ height: 28, fontSize: 12.5 }}
                        >
                          <option value="editor">Editor</option>
                          <option value="viewer">Viewer</option>
                        </select>
                        <button
                          className="btn btn--ghost btn--sm"
                          disabled={busy}
                          title="Transfer ownership"
                          onClick={() => void doTransfer(c.userId, label(c))}
                        >
                          Make owner
                        </button>
                        <button className="btn btn--ghost btn--sm btn--icon" disabled={busy} title="Remove" onClick={() => void doRemove(c.userId)}>
                          {I.x}
                        </button>
                      </>
                    ) : (
                      <span className="chip" style={{ fontSize: 11, textTransform: 'capitalize' }}>{c.role}</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add */}
            {canManage && (
              <>
                <div className="t-micro" style={{ marginBottom: 8 }}>ADD A WORKSPACE MEMBER</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select
                    className="input"
                    value={pickUser}
                    disabled={busy || addable.length === 0}
                    onChange={(e) => setPickUser(e.target.value)}
                    style={{ flex: 1, height: 32 }}
                  >
                    <option value="">
                      {addable.length === 0 ? 'No more members to add' : 'Select a member…'}
                    </option>
                    {addable.map((m) => (
                      <option key={m.id} value={m.id}>
                        {label(m)} ({m.email})
                      </option>
                    ))}
                  </select>
                  <select
                    className="input"
                    value={pickRole}
                    disabled={busy}
                    onChange={(e) => setPickRole(e.target.value as 'editor' | 'viewer')}
                    style={{ height: 32 }}
                  >
                    <option value="editor">Editor</option>
                    <option value="viewer">Viewer</option>
                  </select>
                  <button className="btn btn--accent btn--sm" disabled={busy || !pickUser} onClick={() => void doAdd()}>
                    {I.plus} Share
                  </button>
                </div>
                <div className="t-small muted" style={{ marginTop: 10 }}>
                  Editors can run and improve steps. Viewers have read-only access.
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
